import { useEffect, useRef, useState } from 'react'

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => string
      reset: (id?: string) => void
      remove: (id?: string) => void
    }
  }
}

interface UseTurnstileResult {
  ref: React.RefObject<HTMLDivElement>
  token: string
  reset: () => void
  siteKey: string | undefined
}

/** Renders the Turnstile widget while `active` is true, and tracks its token. */
export function useTurnstile(active: boolean): UseTurnstileResult {
  const [token, setToken] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !active) return
    let cancelled = false
    const tryRender = () => {
      if (cancelled) return
      if (!window.turnstile || !ref.current) {
        window.setTimeout(tryRender, 200)
        return
      }
      widgetIdRef.current = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        callback: (t: string) => setToken(t),
        'expired-callback': () => setToken(''),
        'error-callback': () => setToken('')
      })
    }
    tryRender()
    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [active])

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current)
    setToken('')
  }

  return { ref, token, reset, siteKey: TURNSTILE_SITE_KEY }
}
